import { mailLink, mailOrigin, readMailLink, type MailLinkKind } from "./links.ts";

/**
 * The three account mails (ADR-092 decision 16): confirm your email, reset your password, and your password was
 * changed. Each is a subject, a text part and an HTML part that says the same words: one column, system fonts,
 * inline styles, the link once as an anchor whose text is the link itself, no image, no remote resource and no
 * tracking. They are pure functions, so the mail Worker and the development capture sink render the same bytes.
 * Nothing but the link reaches a mail from a caller, and a link is taken only when it is exactly a game page's
 * own (`readMailLink`), so no caller can put words, markup or another address into one.
 */

/**
 * The mails by kind.
 */
export type MailTemplateKind = "verify" | "reset" | "changed";

/**
 * A mail as it is handed to the sender.
 */
export interface RenderedMail {
  readonly subject: string;
  readonly text: string;
  readonly html: string;
}

/**
 * Who every account mail is from. Nobody reads the mailbox, which each mail says, and no mail carries a
 * `Reply-To`.
 */
export const MAIL_FROM = { name: "OdysseyTales", address: "noreply@odysseytales.net" } as const;

const BODY_STYLE = "margin:0;padding:24px;background:#ffffff;color:#1c1c1c;font-family:system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:16px;line-height:1.5;";
const COLUMN_STYLE = "max-width:560px;margin:0 auto;";
const PARAGRAPH_STYLE = "margin:0 0 16px 0;";
const LINK_STYLE = "color:#1c4fd6;word-break:break-all;";

/**
 * The words of one mail that asks for a link to be opened: its subject and its paragraphs, each paragraph one text
 * whose `\n` ends a line of the text part.
 */
export interface LinkedMailWords {
  readonly subject: string;
  readonly intro: string;
  readonly linkLead: string;
  readonly expiry: string;
  readonly ignore: string;
}

/**
 * The words of the mail that says the password was changed. The link ends the first line of `fix`.
 */
export interface ChangedMailWords {
  readonly subject: string;
  readonly intro: string;
  readonly nothingToDo: string;
  readonly fix: string;
}

/**
 * The two paragraphs that close every mail.
 */
export interface ClosingMailWords {
  readonly noReply: string;
  readonly signature: string;
}

/**
 * Every word of the three mails, which the caller passes in so this package imports no language package.
 */
export interface MailWords {
  readonly verify: LinkedMailWords;
  readonly reset: LinkedMailWords;
  readonly changed: ChangedMailWords;
  readonly closing: ClosingMailWords;
}

/**
 * One paragraph of a mail: its lines as the text part wraps them, and where its link sits, if it has one. A
 * link ends its line in the text part.
 */
interface Paragraph {
  readonly lines: readonly string[];
  readonly link?: { readonly line: number; readonly url: string };
}

/**
 * Escapes text for an HTML body or a double-quoted attribute.
 *
 * @param text The text.
 * @returns The text with the five characters HTML reads as markup written as entities.
 */
function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/**
 * A paragraph as the text part holds it: its lines, the link ending the line it sits on.
 *
 * @param paragraph The paragraph.
 * @returns The lines joined by newlines.
 */
function paragraphText(paragraph: Paragraph): string {
  return paragraph.lines.map((line, index) => (paragraph.link?.line === index ? `${line}${paragraph.link.url}` : line)).join("\n");
}

/**
 * A paragraph as the HTML part holds it: its lines as one run of words, the link an anchor whose text is the
 * link.
 *
 * @param paragraph The paragraph.
 * @returns The `p` element.
 */
function paragraphHtml(paragraph: Paragraph): string {
  const words = paragraph.lines
    .map((line, index) => {
      const link = paragraph.link?.line === index ? paragraph.link.url : null;
      return link === null ? escapeHtml(line) : `${escapeHtml(line)}<a href="${escapeHtml(link)}" style="${LINK_STYLE}">${escapeHtml(link)}</a>`;
    })
    .join(" ");
  return `<p style="${PARAGRAPH_STYLE}">${words}</p>`;
}

/**
 * Builds a mail from its subject and paragraphs.
 *
 * @param subject The subject.
 * @param paragraphs The paragraphs in order, the no-reply sentence and the signature among them.
 * @returns The mail.
 */
function build(subject: string, paragraphs: readonly Paragraph[]): RenderedMail {
  const text = `${paragraphs.map(paragraphText).join("\n\n")}\n`;
  const html = `<!doctype html>\n<html lang="en">\n<head>\n<meta charset="utf-8">\n</head>\n<body style="${BODY_STYLE}">\n<div style="${COLUMN_STYLE}">\n${paragraphs.map(paragraphHtml).join("\n")}\n</div>\n</body>\n</html>\n`;
  return { subject, text, html };
}

/**
 * A link a template may carry: exactly a game page's own for the kind, as `mailLink` builds it.
 *
 * @param link The link.
 * @param kind The kind the template is for.
 * @returns The link, unchanged.
 * @throws RangeError when the text is anything else.
 */
function ownLink(link: string, kind: MailLinkKind): string {
  if (readMailLink(link)?.kind !== kind) {
    throw new RangeError(`a ${kind} mail's link must be the game page's own, as mailLink builds it`);
  }
  return link;
}

/**
 * A paragraph of plain words.
 *
 * @param text The paragraph, its lines separated by `\n`.
 * @returns The paragraph.
 */
function plain(text: string): Paragraph {
  return { lines: text.split("\n") };
}

/**
 * A paragraph whose first line ends with the link, a space before it.
 *
 * @param text The paragraph, its lines separated by `\n`.
 * @param url The link.
 * @returns The paragraph.
 */
function linkEnding(text: string, url: string): Paragraph {
  const [first = "", ...rest] = text.split("\n");
  return { lines: [`${first} `, ...rest], link: { line: 0, url } };
}

/**
 * A paragraph that is only the link.
 *
 * @param url The link.
 * @returns The paragraph.
 */
function linkAlone(url: string): Paragraph {
  return { lines: [""], link: { line: 0, url } };
}

/**
 * The paragraphs every link mail shares around its link.
 *
 * @param words The mail's words.
 * @param common The closing words.
 * @param url The link.
 * @returns The paragraphs in order, the intro first.
 */
function linkedParagraphs(words: LinkedMailWords, common: MailWords, url: string): Paragraph[] {
  return [plain(words.intro), plain(words.linkLead), linkAlone(url), plain(words.expiry), plain(words.ignore), plain(common.closing.noReply), plain(common.closing.signature)];
}

/**
 * The mail that asks a new account to confirm its address.
 *
 * @param link The confirmation link (`mailLink(origin, "verify", token)`).
 * @param words The mails' words.
 * @returns The mail.
 * @throws RangeError when the link is not a game page's own confirmation link.
 */
export function verifyMail(link: string, words: MailWords): RenderedMail {
  const url = ownLink(link, "verify");
  return build(words.verify.subject, linkedParagraphs(words.verify, words, url));
}

/**
 * The mail that carries a link to set a new password.
 *
 * @param link The reset link (`mailLink(origin, "reset", token)`).
 * @param words The mails' words.
 * @returns The mail.
 * @throws RangeError when the link is not a game page's own reset link.
 */
export function resetMail(link: string, words: MailWords): RenderedMail {
  const url = ownLink(link, "reset");
  return build(words.reset.subject, linkedParagraphs(words.reset, words, url));
}

/**
 * The mail that says an account's password was changed, so a change its owner did not make is noticed.
 *
 * @param origin The game page's origin, which the mail names as where to set a new password.
 * @param words The mails' words.
 * @returns The mail.
 * @throws RangeError when the origin is not an origin.
 */
export function changedMail(origin: string, words: MailWords): RenderedMail {
  const url = mailOrigin(origin);
  const changed = words.changed;
  return build(changed.subject, [
    plain(changed.intro),
    plain(changed.nothingToDo),
    linkEnding(changed.fix, url),
    plain(words.closing.noReply),
    plain(words.closing.signature),
  ]);
}

/**
 * Renders a mail by its kind, as the Worker and the capture sink do from a request.
 *
 * @param kind The mail.
 * @param origin The game page's origin.
 * @param token The token a `verify` or `reset` link carries; null for `changed`.
 * @param words The mails' words.
 * @returns The mail.
 * @throws RangeError when `verify` or `reset` is given no token or one out of shape, when `changed` is given
 *   one, or when the origin is not an origin.
 */
export function renderMail(kind: MailTemplateKind, origin: string, token: string | null, words: MailWords): RenderedMail {
  if (kind === "changed") {
    if (token !== null) {
      throw new RangeError("the changed mail carries no token");
    }
    return changedMail(origin, words);
  }
  if (token === null) {
    throw new RangeError(`the ${kind} mail needs a token`);
  }
  const link = mailLink(origin, kind, token);
  return kind === "verify" ? verifyMail(link, words) : resetMail(link, words);
}
